import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { decryptToObjectUrl, type FileEncryption } from './fileCrypto';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibraryOutlined';
import StandardModal, { useModalLoading } from '../modales/StandardModal';
import { useT } from '../../i18n';

interface Attachment {
  id: number;
  url: string | null;
  thumbUrl: string | null;
  createdAt: string;
  latitude: number | null;
  longitude: number | null;
  contentType?: string;
  encryption?: FileEncryption | null;
}

/** Miniatura: si el archivo está cifrado, se descarga y se descifra aquí. */
const Thumb: React.FC<{ a: Attachment; alt: string; failed: string }> = ({ a, alt, failed }) => {
  const [src, setSrc] = useState<string | null>(a.encryption ? null : a.thumbUrl || a.url);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!a.encryption) return undefined;
    let made: string | null = null;
    let alive = true;
    const which = a.thumbUrl ? 'thumb' : 'main';
    decryptToObjectUrl((a.thumbUrl || a.url) as string, a.encryption, which, a.contentType)
      .then((u) => {
        made = u;
        if (alive) setSrc(u);
        else URL.revokeObjectURL(u);
      })
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [a]);
  if (error) return <Typography variant="caption" sx={{ p: 2, display: 'block' }}>{failed}</Typography>;
  if (!src) return <Box sx={{ width: '100%', aspectRatio: '1' }} />;
  return <Box component="img" src={src} alt={alt} sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />;
};

/** Abre el archivo completo; si está cifrado, primero lo descifra. */
const openFull = async (a: Attachment) => {
  if (!a.url) return;
  if (!a.encryption) {
    window.open(a.url, '_blank', 'noopener,noreferrer');
    return;
  }
  const tab = window.open('', '_blank');
  const local = await decryptToObjectUrl(a.url, a.encryption, 'main', a.contentType);
  if (tab) tab.location.href = local;
  else window.open(local, '_blank');
  setTimeout(() => URL.revokeObjectURL(local), 60_000);
}

/** Ver las fotos de un registro (enlaces firmados de R2 que vencen en 10 minutos). */
const PhotoViewerButton: React.FC<{ ownerType: string; ownerId: number; title?: string }> = ({ ownerType, ownerId, title }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Attachment[] | null>(null);
  const { loading, showLoading, hideLoading } = useModalLoading();

  const show = async () => {
    setOpen(true);
    setItems(null);
    showLoading();
    try {
      const { data } = await axios.get('attachments', { params: { ownerType, ownerId } });
      setItems(data.data || []);
    } catch {
      setItems([]);
    } finally {
      hideLoading();
    }
  };

  return (
    <>
      <Tooltip title={t('attachments.viewPhotos')}>
        <IconButton size="small" onClick={() => void show()} aria-label={t('attachments.viewPhotos')}>
          <PhotoLibraryIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <StandardModal open={open} onClose={() => setOpen(false)} title={title || t('attachments.photos')} maxWidth={720} loading={loading}
        actions={<Button size="small" onClick={() => setOpen(false)}>{t('common.close')}</Button>}>
        {items && items.length === 0 && (
          <Typography variant="body2" color="text.secondary">{t('attachments.noPhotos')}</Typography>
        )}
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(min(160px, 100%), 1fr))' }}>
          {(items || []).map((a) => (
            <Box
              key={a.id}
              component="button"
              type="button"
              onClick={() => void openFull(a)}
              aria-label={t('attachments.photo')}
              sx={{ display: 'block', width: '100%', p: 0, cursor: a.url ? 'pointer' : 'default', borderRadius: 1.5, overflow: 'hidden', border: 1, borderColor: 'divider', bgcolor: 'action.hover' }}
            >
              {a.thumbUrl || a.url ? (
                <Thumb a={a} alt={t('attachments.photo')} failed={t('attachments.decryptFailed')} />
              ) : (
                <Typography variant="caption" sx={{ p: 2, display: 'block' }}>{t('attachments.pending')}</Typography>
              )}
            </Box>
          ))}
        </Box>
      </StandardModal>
    </>
  );
};

export default PhotoViewerButton;
